"use client";

import { ArrowRight, Calculator, Wallet, BookOpen, ChartLine as LineChart, Compass, ShieldCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SpecializedInspection() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const services = [{
    number: "01",
    title: "Mold Inspection",
    description: "Comprehensive visual and physical assessment to identify hidden mold growth and moisture sources in your property.",
    Icon: Calculator
  }, {
    number: "02",
    title: "Air Quality Testing",
    description: "Professional air sampling to detect airborne mold spores and allergens, ensuring your indoor environment is safe.",
    Icon: Wallet
  }, {
    number: "03",
    title: "Moisture Mapping",
    description: "Advanced thermal imaging and moisture detection to pinpoint water intrusion points before mold takes hold.",
    Icon: BookOpen
  }, {
    number: "04",
    title: "Surface Sampling",
    description: "Laboratory-grade surface testing to identify specific mold species and determine the severity of contamination.",
    Icon: LineChart
  }, {
    number: "05",
    title: "Post-Remediation Verification",
    description: "Certified clearance testing to confirm that your property is free of mold after professional remediation work.",
    Icon: Compass
  }, {
    number: "06",
    title: "Environmental Consulting",
    description: "Expert guidance on indoor environmental health, tailored to your specific property and safety concerns.",
    Icon: ShieldCheck
  }];
  return <section data-section-id="241" id="specialized-inspection-services" className="bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-default italic text-base text-primary/70">02</span>
              <span className="h-px w-8 bg-border" />
              <span className="font-mono uppercase tracking-widest text-xs text-primary">
                Our Services
              </span>
            </div>
            <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tighter text-foreground">
              Clinical precision for your{" "}
              <span className="italic font-light text-primary">indoor environment.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-4">
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-md lg:ml-auto">
              With 26 years of certified expertise, MYB Services provides 
              evidence-forward testing to ensure your home or business remains a safe, 
              healthy space for your family and employees.
            </p>
          </div>
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
          {services.map((service, i) => {
          const Icon = service.Icon;
          return <div key={i} data-index={i} className={`group relative bg-card p-8 md:p-10 border border-transparent hover:border-primary transition-all duration-500 ease-out hover:shadow-lg ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            transitionDelay: gridVisible ? `${i * 80}ms` : "0ms"
          }}>
                {}
                <span className="absolute top-6 right-6 font-mono text-3xl text-muted-foreground/20 group-hover:text-primary transition-colors duration-500">
                  {service.number}
                </span>

                {}
                <div className="mb-10">
                  <Icon className="w-10 h-10 text-primary" strokeWidth={1.5} aria-hidden="true" />
                </div>

                {}
                <h3 className="font-default font-semibold text-2xl text-foreground mb-3 leading-snug">
                  {service.title}
                </h3>

                {}
                <p className="text-sm md:text-base leading-relaxed text-muted-foreground mb-8 max-w-sm">
                  {service.description}
                </p>

                {}
                <Link to="/ServicesPage" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-wider text-primary hover:text-primary/80 transition-colors duration-300">
                  <span>View details</span>
                  <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </div>;
        })}
        </div>

        {}
        <div ref={ctaRef} className={`mt-16 md:mt-20 text-center transition-all duration-700 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Button asChild variant="default" className="bg-primary text-white rounded-lg px-8 py-6 font-default font-semibold hover:bg-primary/90 transition-all duration-300">
            <Link to="/ContactPage" className="inline-flex items-center gap-2">
              <span>Schedule Your Inspection</span>
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}